import { OfferStore } from '../api/dto/offers.dto.js';
import type { ValidatePurchaseRequest } from '../api/dto/offers.dto.js';
import { TelegramStars } from './telegram_stars.js';
import { TonPayments } from './ton_payments.js';

export interface IapPurchaseResult
{
  product_id?: string;
  transaction_id?: string;
  original_transaction_id?: string;
  purchase_token?: string;
  order_id?: string;
  currency?: string;
  price_micros?: number | string;
  environment?: 'production' | 'sandbox';
}

export interface IapProductDetails
{
  product_id: string;
  title?: string;
  description?: string;
  formatted_price?: string;
  currency?: string;
  price_micros?: number | string;
}

export interface IapConsumeResult
{
  ok: boolean;
  consumed: boolean;
  purchase_token?: string;
}

export interface IapAcknowledgeResult
{
  ok: boolean;
  acknowledged: boolean;
  purchase_token?: string;
}

function getPlatformStore(): OfferStore | null
{
  const platform = window.Capacitor?.getPlatform?.();
  if ( platform === 'android' ) return OfferStore.GooglePlay;
  if ( platform === 'ios' ) return OfferStore.AppStore;
  if ( TelegramStars.isAvailable() ) return OfferStore.TelegramStars;
  // Web build outside Telegram and the stores: crypto checkout, if the server sent crypto prices.
  if ( TonPayments.isAvailable() ) return OfferStore.Ton;
  return null;
}

function getPlugin(): any
{
  const plugins = ( window as any )?.Capacitor?.Plugins ?? null;
  if ( !plugins ) return null;
  return plugins.IAP ?? plugins.InAppPurchase ?? plugins.Billing ?? plugins.Purchases ?? null;
}

function normalizePurchaseResult(
  store: OfferStore,
  product_id: string,
  result: IapPurchaseResult | Record<string, unknown> | undefined,
): ValidatePurchaseRequest
{
  const raw = ( result ?? {} ) as Record<string, unknown>;
  const purchase_token = raw.purchase_token ?? raw.purchaseToken ?? raw.token;
  const transaction_id = raw.transaction_id ?? raw.transactionId ?? raw.order_id ?? raw.orderId;
  const original_transaction_id = raw.original_transaction_id ?? raw.originalTransactionId;
  const currency = raw.currency;
  const price_micros = raw.price_micros ?? raw.priceMicros;
  const environment = raw.environment;

  return {
    store,
    product_id: String( raw.product_id ?? raw.productId ?? product_id ),
    transaction_id: typeof transaction_id === 'string' ? transaction_id : undefined,
    original_transaction_id: typeof original_transaction_id === 'string' ? original_transaction_id : undefined,
    purchase_token: typeof purchase_token === 'string' ? purchase_token : undefined,
    currency: typeof currency === 'string' ? currency : undefined,
    price_micros: typeof price_micros === 'number' || typeof price_micros === 'string' ? price_micros : undefined,
    environment: environment === 'production' || environment === 'sandbox' ? environment : undefined,
  };
}

export const IapPlugin = {
  getStore(): OfferStore | null
  {
    return getPlatformStore();
  },

  isAvailable(): boolean
  {
    const store = getPlatformStore();
    if ( store === OfferStore.TelegramStars || store === OfferStore.Ton ) return true;
    return !!getPlugin() && !!store;
  },

  async purchaseProduct( product_id: string ): Promise<ValidatePurchaseRequest>
  {
    const store = getPlatformStore();
    if ( !store ) throw new Error( 'IAP is only available on Android/iOS builds' );
    // Stars are bought per offer (OfferService), not per SKU, and leave no client receipt.
    if ( store === OfferStore.TelegramStars ) throw new Error( 'telegram_stars_requires_offer_purchase' );
    if ( store === OfferStore.Ton ) throw new Error( 'ton_requires_offer_purchase' );

    const plugin = getPlugin();
    if ( !plugin ) throw new Error( 'IAP native plugin is not available' );

    const result = plugin.purchaseProduct
      ? await plugin.purchaseProduct( { productId: product_id, product_id } )
      : plugin.purchase
        ? await plugin.purchase( { productId: product_id, product_id } )
        : plugin.buy
          ? await plugin.buy( { productId: product_id, product_id } )
          : null;

    if ( result == null ) throw new Error( 'IAP plugin does not expose a purchase method' );
    return normalizePurchaseResult( store, product_id, result );
  },

  async consumePurchase( purchase: Pick<ValidatePurchaseRequest, 'store' | 'purchase_token'> ): Promise<IapConsumeResult | null>
  {
    if ( purchase.store !== OfferStore.GooglePlay || !purchase.purchase_token ) return null;

    const plugin = getPlugin();
    if ( !plugin?.consumePurchase ) return null;

    const result = await plugin.consumePurchase( {
      purchaseToken: purchase.purchase_token,
      purchase_token: purchase.purchase_token,
      token: purchase.purchase_token,
    } );

    const raw = ( result ?? {} ) as Record<string, unknown>;
    return {
      ok: raw.ok === true,
      consumed: raw.consumed === true,
      purchase_token: typeof raw.purchase_token === 'string'
        ? raw.purchase_token
        : typeof raw.purchaseToken === 'string'
          ? raw.purchaseToken
          : undefined,
    };
  },

  async consumeOwnedProduct( product_id: string ): Promise<IapConsumeResult | null>
  {
    if ( getPlatformStore() !== OfferStore.GooglePlay ) return null;

    const plugin = getPlugin();
    if ( !plugin?.consumeOwnedProduct ) return null;

    const result = await plugin.consumeOwnedProduct( { productId: product_id, product_id } );
    const raw = ( result ?? {} ) as Record<string, unknown>;
    return {
      ok: raw.ok === true,
      consumed: raw.consumed === true,
      purchase_token: typeof raw.purchase_token === 'string'
        ? raw.purchase_token
        : typeof raw.purchaseToken === 'string'
          ? raw.purchaseToken
          : undefined,
    };
  },

  async acknowledgePurchase( purchase: Pick<ValidatePurchaseRequest, 'store' | 'purchase_token'> ): Promise<IapAcknowledgeResult | null>
  {
    if ( purchase.store !== OfferStore.GooglePlay || !purchase.purchase_token ) return null;

    const plugin = getPlugin();
    if ( !plugin?.acknowledgePurchase ) return null;

    const result = await plugin.acknowledgePurchase( {
      purchaseToken: purchase.purchase_token,
      purchase_token: purchase.purchase_token,
      token: purchase.purchase_token,
    } );

    const raw = ( result ?? {} ) as Record<string, unknown>;
    return {
      ok: raw.ok === true,
      acknowledged: raw.acknowledged === true,
      purchase_token: typeof raw.purchase_token === 'string'
        ? raw.purchase_token
        : typeof raw.purchaseToken === 'string'
          ? raw.purchaseToken
          : undefined,
    };
  },

  async getProductDetails( product_id: string ): Promise<IapProductDetails | null>
  {
    if ( getPlatformStore() === OfferStore.TelegramStars )
    {
      const price_stars = TelegramStars.getPrice( product_id );
      return price_stars === null ? null : {
        product_id,
        formatted_price: TelegramStars.formatPrice( price_stars ),
        currency: 'XTR',
      };
    }
    if ( getPlatformStore() === OfferStore.Ton )
    {
      const formatted_price = TonPayments.formatPrice( product_id );
      const asset = TonPayments.defaultAsset( product_id );
      return formatted_price === null || asset === null ? null : { product_id, formatted_price, currency: asset };
    }

    const plugin = getPlugin();
    if ( !plugin?.getProductDetails ) return null;

    const result = await plugin.getProductDetails( { productId: product_id, product_id } );
    if ( !result ) return null;

    const raw = result as Record<string, unknown>;
    const formatted_price = raw.formatted_price ?? raw.formattedPrice;
    const price_micros = raw.price_micros ?? raw.priceMicros;

    return {
      product_id: String( raw.product_id ?? raw.productId ?? product_id ),
      title: typeof raw.title === 'string' ? raw.title : undefined,
      description: typeof raw.description === 'string' ? raw.description : undefined,
      formatted_price: typeof formatted_price === 'string' ? formatted_price : undefined,
      currency: typeof raw.currency === 'string' ? raw.currency : undefined,
      price_micros: typeof price_micros === 'number' || typeof price_micros === 'string' ? price_micros : undefined,
    };
  },

  async restorePurchases(): Promise<ValidatePurchaseRequest[]>
  {
    const store = getPlatformStore();
    if ( !store || store === OfferStore.TelegramStars || store === OfferStore.Ton ) return [];

    const plugin = getPlugin();
    if ( !plugin?.restorePurchases ) return [];

    const result = await plugin.restorePurchases();
    const raw_purchases: Record<string, unknown>[] = Array.isArray( result?.purchases ) ? result.purchases : [];
    return raw_purchases.map( ( item: Record<string, unknown> ) =>
      normalizePurchaseResult( store, String( item.product_id ?? item.productId ?? '' ), item )
    ).filter( item => !!item.product_id && !!item.purchase_token );
  },
};
